import { ENABLE_I18N } from '@src/config/env'
import { TAbsoluteLocale, TCountry, TCountryLanguage, LOCALE_FORMAT, DEFAULT_COUNTRY } from '@src/config/i18n.config'
import QuinceApiError from '@src/quince-site-common/src/domains/quince-api-error'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import {
  customerServiceClient,
  reportingServicesHttpClient,
  storefrontRegionsHttpClient,
} from '@src/quince-site-common/src/utils/HttpClient'

import { parseFormat } from './general.utils'
export const parseLocale = (locale: TAbsoluteLocale): { country: TCountry; language: TCountryLanguage<TCountry> } => {
  return parseFormat(LOCALE_FORMAT, locale) as {
    country: TCountry
    language: TCountryLanguage<TCountry>
  }
}

export const getStandardLocale = (locale: TAbsoluteLocale) => {
  const parsedLocale = parseLocale(locale)
  return `${parsedLocale.language}-${parsedLocale.country.toUpperCase()}`
}

export const getShortenedLocale = (locale: TAbsoluteLocale, regionDetails: TRegionDetail) => {
  const { country } = parseLocale(locale)
  if (locale === regionDetails.regions[DEFAULT_COUNTRY]?.defaultLanguageIsoCode) return ''
  if (locale === regionDetails.regions[country]?.defaultLanguageIsoCode) return country
  return locale
}

/**
 * Returns the value associated with the primaryKey if it exists in the object.
 * Otherwise, it returns the value associated with the fallbackKey.
 *
 * @param {T} obj - The object to search for the keys.
 * @param {K} primaryKey - The primary key to look for in the object.
 * @param {L} fallbackKey - The fallback key to use if the primary key is not found.
 * @returns {T[K]} - The value associated with the primaryKey or fallbackKey.
 */
export function getDisplayKey<T, K extends keyof T, L>(obj: T, primaryKey: K, fallback: L) {
  if (!ENABLE_I18N) return fallback
  return obj[primaryKey] ?? fallback
}

export interface TSupportedLanguage {
  isoCode: TAbsoluteLocale
  displayName: string
}

export interface TResponseRegionDetail {
  regionId: number
  regionCode: TCountry
  internalName: string
  displayName: string
  defaultLanguageIsoCode: TAbsoluteLocale
  defaultCurrencyIsoCode: string
  supportedLanguagesIsoCodes: TAbsoluteLocale[]
  supportedLanguages: TSupportedLanguage[]
}

export interface TRegionDetail {
  regionCodes: TCountry[]
  regions: Partial<
    Record<
      TCountry,
      Omit<TResponseRegionDetail, 'supportedLanguages'> & {
        supportedLanguages: Record<TAbsoluteLocale, TSupportedLanguage>
      }
    >
  >
}

export const fetchRegionDetails = async (): Promise<TRegionDetail> => {
  if (!ENABLE_I18N)
    return {
      regionCodes: [],
      regions: {},
    }
  try {
    const res = await storefrontRegionsHttpClient.get(`/regions/all/lite?options=ENRICH_SUPPORTED_LANGUAGES`)
    const regionDetails = (await res.data) as TResponseRegionDetail[]

    const regions = regionDetails.reduce(
      (acc, region) => ({
        ...acc,
        [region.regionCode]: {
          ...region,
          supportedLanguages: region.supportedLanguages.reduce(
            (acc, supportedLanguage) => ({
              ...acc,
              [supportedLanguage.isoCode]: supportedLanguage,
            }),
            {},
          ),
        },
      }),
      {},
    )

    return {
      regionCodes: regionDetails.map(region => region.regionCode),
      regions,
    }
  } catch (err) {
    return {
      regionCodes: [],
      regions: {},
    }
  }
}

export const updateCommunicationPreference = async ({
  locale,
  regionDetails,
  abortController,
}: {
  locale: TAbsoluteLocale
  regionDetails: TRegionDetail
  abortController: AbortController | null
}) => {
  const country = parseLocale(locale).country
  const regionId = regionDetails.regions[country]?.regionId
  const loggedIn = AuthUtil.isLoggedIn()
  if (!regionId || !loggedIn) return

  try {
    const response = await customerServiceClient.post(
      '/communication-preference',
      {
        preferredLanguage: locale,
        regionId,
      },
      {
        signal: abortController?.signal,
      },
    )
    return response
  } catch (error) {
    if (abortController?.signal.aborted) return
    console.error('Failed to update communication preference: ', error)
    throw new QuinceApiError({
      error: error as Error,
      message: 'Failed to update communication preference',
      apiName: 'communication-preference',
    })
  }
}

export const getPreferredLanguage = async () => {
  try {
    const res = (await reportingServicesHttpClient.get('/customers/meta')) as {
      data: {
        preferredLanguage: TCountryLanguage<TCountry> | null
      }
    }
    return res.data.preferredLanguage
  } catch (error) {
    console.error('Failed to get preferred language: ', error)
    return null
  }
}

export const getAbsoluteLocale = (
  locale: TCountry | TAbsoluteLocale | '',
  regionDetails: TRegionDetail,
): TAbsoluteLocale => {
  if (locale === '') {
    return regionDetails.regions[DEFAULT_COUNTRY]?.defaultCurrencyIsoCode as TAbsoluteLocale
  }
  if (Object.hasOwn(regionDetails.regions, locale)) {
    return regionDetails.regions[locale as TCountry]?.defaultLanguageIsoCode as TAbsoluteLocale
  }
  return locale as TAbsoluteLocale
}
